import { observer } from 'mobx-react-lite';
import React, { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDebouncedCallback } from 'use-debounce/lib';
import { ISelectProps, Select } from '../../npaper';
import {
  IProductGroup,
  IProductGroupSnapshotOut,
} from '../../stores/products/ProductGroupStore';
import { useStores } from '../../stores/Root';
import { IPaginationFilter } from '../../stores/utils/modelWithCRUD';

export interface IProductGroupSelectProps extends ISelectProps<IProductGroup> {
  filter?: IPaginationFilter<IProductGroupSnapshotOut>;
}

export const ProductGroupSelect: React.FC<IProductGroupSelectProps> = observer(
  ({
    children,
    query: propQuery = '',
    placeholder: defaultPlaceholder,
    title,
    selected = null,
    onSelect,
    filter = {},
    disabled = false,
    small,
    onOpen,
    hideSearchInput = false,
    closesOnSelect = true,
    placement,
  }) => {
    const intl = useIntl();
    const placeholder =
      defaultPlaceholder ||
      intl.formatMessage({ defaultMessage: 'Choose a group' });

    const { productGroupStore } = useStores();

    const [opened, setOpened] = useState(false);
    const [innerSelected, setInnerSelected] =
      useState<IProductGroup | null>(null);

    useEffect(() => {
      let isSub = true;
      if (selected) {
        productGroupStore
          .getItem(selected)
          .then((group) => isSub && setInnerSelected(group));
      } else {
        setInnerSelected(null);
      }
      return () => {
        isSub = false;
      };
    }, [productGroupStore, selected]);

    const [items, setItems] = useState<IProductGroup[]>();
    const [query, setQuery] = useState(propQuery);
    const debouncedQuery = useDebouncedCallback(setQuery, 150);

    useEffect(() => {
      debouncedQuery.callback(propQuery);
    }, [debouncedQuery, propQuery]);

    useEffect(() => {
      if (!opened) return;

      let isSub = true;
      productGroupStore
        .fetchMore({ filter: { ...filter, search: query } })
        .then((res) => isSub && setItems(res.items));

      return () => {
        isSub = false;
      };
    }, [productGroupStore, filter, query, opened]);

    return (
      <Select
        placement={placement}
        closesOnSelect={closesOnSelect}
        title={title}
        placeholder={placeholder}
        disabled={disabled}
        hideSearchInput={hideSearchInput}
        isOpen={opened}
        onOpen={() => {
          setOpened(true);
          onOpen && onOpen();
        }}
        onClose={() => {
          setOpened(false);
        }}
        small={small}
        onQueryChange={debouncedQuery.callback}
        items={items}
        isLoading={!items || productGroupStore.isLoading}
        resolveSelectedValue={(id) => productGroupStore.getItem(id)}
        itemRenderer={(item) => item.name}
        onSelect={(contact) => {
          setInnerSelected(contact);
          onSelect && onSelect(contact);
        }}
        value={innerSelected ? innerSelected.id : undefined}
        valueRenderer={(selected) => selected.name}
      >
        {children}
      </Select>
    );
  }
);
